import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { TealiumAnalyticsService } from '@ddt-web/analytics';
import { ERROR_SEVERITY, ErrorDCR } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { AbstractErrorHandlerService, ErrorHandlerUtil, ErrorHelperService, ErrorObject, ErrorPopupService } from '@ddt-web/error-handler';

import { FundStatusInteractionData } from '../models';


@Injectable({
	providedIn: 'root'
})
export class FundStatusInteractionErrorHandlerService extends AbstractErrorHandlerService {

	private static readonly TRACK_ERROR_MESSAGES: { [configName: string]: string } = {
		[ErrorDCR.FUND_STATUS_INTERACTION_MISSING_ID_ERROR]: 'missingfundstatusinteractionid',
		[ErrorDCR.FUND_STATUS_INTERACTION_API_FAILURE_ERROR]: 'fundstatusinteractionapifailure',
		[ErrorDCR.FUND_STATUS_INTERACTION_FUND_LIMIT_EXCEEDED_ERROR]: 'fundstatusinteractionfundlimitexceeded',
		[ErrorDCR.FUND_STATUS_INTERACTION_MISSING_SECONDARY_IDENTIFIERS_ERROR]: 'missingsecondaryfundstatusinteractionidentifiers'
	};

	@InjectConfig(SharedConfigKey.MAXIMUM_STATUS_INTERACTIONS) private readonly maxStatusInteractions: number = 250;

	constructor(
		protected contentService: ContentService,
		protected errorPopupService: ErrorPopupService,
		protected tealiumAnalyticsService: TealiumAnalyticsService,
		protected errorHelperService: ErrorHelperService
	) {
		super(contentService, errorPopupService, tealiumAnalyticsService, errorHelperService);
	}
	public handleError(error: ErrorObject): void {
		const isFundStatusInteractionError: boolean = Object.keys(FundStatusInteractionErrorHandlerService.TRACK_ERROR_MESSAGES).includes(error.configName);

		if (isFundStatusInteractionError) {
			this.errorSeverity = ERROR_SEVERITY.WARNING;
			this.trackErrorMessage = FundStatusInteractionErrorHandlerService.TRACK_ERROR_MESSAGES[error.configName];
			this.tealiumAnalyticsService.trackError(this.trackErrorMessage, this.errorSeverity);

			if (error.configName === ErrorDCR.FUND_STATUS_INTERACTION_MISSING_SECONDARY_IDENTIFIERS_ERROR) {
				console.warn(
					'Fund status interaction data is missing some of the secondary fund identifiers. ' +
					'Will proceed storing fund status interactions but the fund identifier data in the interaction records will be incomplete.',
					error
				);
			} else {
				const tickers: string[] = (error.responseJson.fundPreferences as FundStatusInteractionData[] ?? [])
					.map(interactionData => interactionData.ticker)
					.filter(ticker => !!ticker);

				let message = ErrorHandlerUtil.getUserErrorMessage(
					{ dcrKey: 'FUND_STATUS_INTERACTION_STORAGE_FAILURE', dcrValue: 'Failed to update status interactions for the following funds: $tickers$' },
					{ dcrKey: 'FUND_STATUS_INTERACTION_STORAGE_GENERIC_FAILURE', dcrValue: 'Something went wrong while storing status interactions. Please retry.' },
					new Map([['$tickers$', tickers.join(', ')]]),
					!tickers.length,
					this.contentService
				);
				if (error.configName === ErrorDCR.FUND_STATUS_INTERACTION_FUND_LIMIT_EXCEEDED_ERROR) {
					message = this.contentService.getSubstitutedValuesString(
						'FUND_STATUS_INTERACTION_FUND_LIMIT_EXCEEDED',
						new Map([['$maxLimit$', this.maxStatusInteractions.toString()]]),
						'You may only have $maxLimit$ status interactions with funds at a time. Please remove some of your status interactions to proceed.'
					);
				}
				const actions = [ErrorHandlerUtil.createErrorContinueButton(this.contentService)];

				console.error(`${message} - ${error.configName}`, error);
				this.errorPopupService.openResponseMessageDialog(
					this.buildErrorMessage(false, 'error-fund-status-interactions-dialog', this.errorMessage, this.trackErrorMessage, message, true, tickers, actions)
				);
			}
		} else {
			this.nextHandler.handleError(error);
		}
	}
}
